import './devices-associations.scss';
import {Box, Button, Stack, Typography} from '@mui/material';
import {MutableRefObject, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {Device} from '../../backend/interfaces/device';
import {PutDevice} from '../../backend/requests/put-device';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {usePaginatedTable} from '../../hooks/paginated-table';
import {translations} from '../../i18n/translations';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {DevicesRepository} from '../../repositories/devices';
import {PageHeader} from '../page-header/page-header';
import {PaginatedTable} from '../paginated-table/paginated-table';
import {getBrandName} from '../../utils/functions/get-product-name';
import {useAppContext} from '../../contexts/app/context';
import AddIcon from '@mui/icons-material/Add';
import {CaregiverMode} from '../../enums';
import GenericHeader from '../generic-header/generic-header';
import {Modal} from '../modal/modal';
import {useFetchAllItems} from '../../hooks/fetch-all-items';
import {DeviceWorkgroup} from '../../backend/enums/device-workgroup';
import {FlatSelectDropdown} from '../flat-select-dropdown/flat-select-dropdown';

// Columns IDs
enum ColumnIdentifier {
  Identifier = 'identifier',
  Type = 'type',
  State = 'state',
  RemoveDevice = 'remove-device',
}

/**
 * Listing of all the devices associated with a user.
 * On this screen, it's possible to remove an association.
 *
 * Only system administrators and professional caregivers that are managers or
 * administrators can associate/dissociate devices.
 * All other professional caregivers can only view the devices listing.
 *
 * Permissions are given by props.
 */
export const DevicesAssociations = (props: {
  caregiverMode: CaregiverMode.SystemAdministrator | CaregiverMode.Professional;
  canWrite: boolean;
  userId: string;
  userName: string;
  deviceRowBaseLinkUrl: string;
  entityId?: string;
  associateDeviceUrl?: string;
  headerTitleCrumbs?: {label: string; link: string}[];
  headerTitle?: string;
}) => {
  const [removeDeviceDialogOpened, setRemoveDeviceDialogOpened] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [addDeviceDialogOpened, setAddDeviceDialogOpened] = useState<boolean>(false);
  const [confirmAssociateDeviceDialogOpened, setConfirmAssociateDeviceDialogOpened] =
    useState<boolean>(false);
  const [associateDevice, setAssociateDevice] = useState<Device | undefined>(undefined);
  const deviceToRemoveRef: MutableRefObject<{identifier: string; device?: Device} | undefined> =
    useRef();
  const snackBarContext = useSnackBar();
  const appContext = useAppContext();
  const {t} = useTranslation();
  const {items: institutionDevices, refreshItems} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      if (!props.entityId) {
        return Promise.resolve({data: [], totalCount: 0});
      }
      return DevicesRepository.instance.getInEntityAsAffiliatedForEntity(
        props.entityId,
        limit,
        skip,
        true,
        undefined,
        DeviceWorkgroup.DigitalInclusion
      );
    },
    deps: [props.entityId],
    enabled: props.caregiverMode === CaregiverMode.Professional,
  });

  const handleGetDevices = async (limit: number, skip: number) => {
    let result: {totalCount: number; data: Array<Device>} = {totalCount: 0, data: []};

    if (props.caregiverMode === CaregiverMode.Professional) {
      if (!props.entityId) {
        throw new Error('Empty entity id.');
      }

      result = await DevicesRepository.instance.getForUserAsAffiliatedForEntity(
        props.userId,
        limit,
        skip,
        props.entityId
      );
    } else if (props.caregiverMode === CaregiverMode.SystemAdministrator) {
      result = await DevicesRepository.instance.getAsSystemAdministrator(
        limit,
        skip,
        undefined,
        props.userId,
        false,
        undefined
      );
    } else {
      throw new Error('No permissions.');
    }

    // Process data
    const processedData: PaginatedTableData<Device> = result.data
      .filter(
        (device) =>
          device.userSignInType !== 'code' &&
          (props.caregiverMode === CaregiverMode.Professional ||
            props.caregiverMode === CaregiverMode.SystemAdministrator)
      )
      .map((obj) => {
        // Set identifier
        // Note: The serial number is always the identification of any type of device.
        // However, some legacy devices may not have a serial number at the moment, so
        // we need to show the mac address in those cases.
        const identifier: string = obj.serialNumber || obj.macAddress || '';

        // Columns
        const columns: Array<{
          columnId: string;
          value?: string;
        }> = [
          {
            columnId: ColumnIdentifier.Identifier,
            value: identifier,
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.devices_associations.state_value_true)
              : t(translations.devices_associations.state_value_false),
          },
          {
            columnId: ColumnIdentifier.Type,
            value: t(translations.device_types[obj.type]) || '',
          },
        ];

        // Return row values
        return {
          id: obj.id || '',
          item: obj,
          enabled: obj.isActive,
          toLink:
            obj.userSignInType === 'code'
              ? ''
              : props.deviceRowBaseLinkUrl.charAt(props.deviceRowBaseLinkUrl.length - 1) === '/'
                ? `${props.deviceRowBaseLinkUrl}${obj.id}`
                : `${props.deviceRowBaseLinkUrl}/${obj.id}`,
          columns: props.canWrite
            ? [...columns, {columnId: ColumnIdentifier.RemoveDevice}]
            : columns,
        };
      });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {data, isLoading, error, pagesCount, currentPage, handlePageChange, handleApplyFilters} =
    usePaginatedTable<Device>({
      fetchData: handleGetDevices,
    });

  // Ask to remove the device from the user
  const handleAskRemoveDevice = (deviceId: string) => {
    // Get the row
    const row = data.find((obj) => obj.id === deviceId);

    // Set device for later
    deviceToRemoveRef.current = {
      device: row?.item,
      identifier:
        row?.columns.find((obj) => obj.columnId === ColumnIdentifier.Identifier)?.value || '',
    };

    // Open modal
    setRemoveDeviceDialogOpened(true);
  };

  // Don't remove the device
  const handleDeclineRemoveDevice = () => {
    // Close modal
    setRemoveDeviceDialogOpened(false);

    // Clear
    deviceToRemoveRef.current = undefined;
  };

  // Remove device
  const handleRemoveDevice = async () => {
    setIsSubmitting(true);
    try {
      // Verify
      if (!deviceToRemoveRef.current || !deviceToRemoveRef.current.device) {
        // Throw error
        throw new Error('Empty device.');
      }

      // Request body
      const requestBody: PutDevice = {
        isActive: deviceToRemoveRef.current.device.isActive,
        product: deviceToRemoveRef.current.device.product,
        type: deviceToRemoveRef.current.device.type,
        userId: null,
      };

      // Dissociate the device
      await DevicesRepository.instance.updateById(
        deviceToRemoveRef.current.device.id || '',
        requestBody
      );

      // Refresh page
      if (data.length - 1 === 0 && currentPage > 1) {
        // Refresh to the previous page because the current page will be empty
        handleApplyFilters(currentPage - 1);
      } else {
        // Refresh the same page because we still have data on it
        handleApplyFilters();
      }
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.devices_associations.remove_device_success_message, {
          identifier: deviceToRemoveRef.current?.device?.id,
        }),
      });

      // Log
      console.debug(`🟢 Device removed.`, {
        deviceId: deviceToRemoveRef.current?.device?.id,
        userId: props.userId,
        userName: props.userName,
      });

      setIsSubmitting(false);
      setRemoveDeviceDialogOpened(false);
      refreshItems();
      deviceToRemoveRef.current = undefined;
    } catch (error) {
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.devices_associations.remove_device_error_message, {
          identifier: deviceToRemoveRef.current?.device?.id,
        }),
      });
      setIsSubmitting(false);
      setRemoveDeviceDialogOpened(false);
      // Log
      console.debug(`🔴 Could not remove the device.`, {
        deviceId: deviceToRemoveRef.current?.device?.id,
        userId: props.userId,
        userName: props.userName,
        error: error,
      });

      // Clear
      deviceToRemoveRef.current = undefined;
    }
  };

  const handleAssociateDevice = async () => {
    setIsSubmitting(true);
    if (!associateDevice || !associateDevice.id) {
      throw new Error('Empty device.');
    }
    const requestBody: PutDevice = {
      isActive: associateDevice.isActive,
      product: associateDevice.product,
      type: associateDevice.type,
      userId: props.userId,
    };
    try {
      await DevicesRepository.instance.updateById(associateDevice.id, requestBody);
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.associate_device_form.success.success_message, {
          name: props.userName,
        }),
      });
      handleApplyFilters();
      setIsSubmitting(false);
      setAssociateDevice(undefined);
      setConfirmAssociateDeviceDialogOpened(false);
      refreshItems();
    } catch (error) {
      console.debug('🔴 Could not associate the device.', error);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.associate_device_form.errors.error_message, {
          name: props.userName,
        }),
      });
      setIsSubmitting(false);
      setAssociateDevice(undefined);
      setConfirmAssociateDeviceDialogOpened(false);
    }
  };

  const handleOpenAssociateDeviceConfirmation = () => {
    setAddDeviceDialogOpened(false);
    setConfirmAssociateDeviceDialogOpened(true);
  };

  const handleCloseAssociateDeviceDialogs = () => {
    setAddDeviceDialogOpened(false);
    setConfirmAssociateDeviceDialogOpened(false);
    setAssociateDevice(undefined);
  };

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      {props.entityId && (props.headerTitleCrumbs || props.headerTitle) && (
        <GenericHeader
          pageTitle={props.headerTitle || ''}
          titleCrumbs={props.headerTitleCrumbs ? props.headerTitleCrumbs : []}
          isLoading={false}
          leftColumn={<></>}
          rightColumn={
            <>
              <Button
                variant="inverted"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setAddDeviceDialogOpened(true)}
                disabled={!props.canWrite}
              >
                {t(translations.devices_associations.associate_device_button)}
              </Button>
            </>
          }
        />
      )}
      {props.associateDeviceUrl && props.caregiverMode === CaregiverMode.SystemAdministrator && (
        <PageHeader
          rightButtons={
            props.canWrite && (
              <Button
                variant="contained"
                color="primary"
                endIcon={<AddIcon />}
                component={Link}
                to={props.associateDeviceUrl}
                disabled={!props.canWrite}
              >
                {t(translations.devices_associations.associate_device_button)}
              </Button>
            )
          }
        />
      )}
      <Box
        className={`devices-listing-container${props.caregiverMode === CaregiverMode.SystemAdministrator ? '-sys' : ''}`}
      >
        <Box className="devices-listing-container-centered">
          <PaginatedTable
            emptyTableMessage={t(translations.devices_associations.no_devices)}
            columns={[
              {
                columnId: ColumnIdentifier.Identifier,
                title: t(translations.devices_associations.identifier_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.Type,
                title: t(translations.devices_associations.type_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.devices_associations.state_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.RemoveDevice,
                title: t(translations.devices_associations.remove_device_header),
                flexGrow: 1,
                button: {
                  type: 'text-button',
                  showColumnValue: false,
                  disabled: !props.canWrite,
                  disabledTooltip: t(
                    translations.devices_associations.remove_device_button_disabled_tooltip
                  ),
                  textButton: {
                    label: t(translations.devices_associations.remove_device_button),
                    color: 'error',
                    handleClick: handleAskRemoveDevice,
                  },
                },
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={true}
          />
        </Box>
      </Box>
      {props.canWrite && (
        <Modal
          open={removeDeviceDialogOpened}
          onClose={handleDeclineRemoveDevice}
          maxWidth="sm"
          fullWidth
          className="devices-listing-modal"
          preventCloseClick
          actions={[
            {
              label: t(translations.modal.cancel),
              type: 'destructive',
              onClick: handleDeclineRemoveDevice,
            },
            {
              label: t(translations.modal.confirm),
              type: 'nondestructive',
              onClick: handleRemoveDevice,
              loading: isSubmitting,
            },
          ]}
        >
          <Stack>
            <Typography className="title">
              {t(translations.devices_associations.remove_device_dialog_title, {
                name: props.userName,
                identifier: deviceToRemoveRef.current?.identifier || '',
              })}
            </Typography>
            <Typography className="subtitle">
              {t(translations.devices_associations.remove_device_dialog_message, {
                name: props.userName,
                brand: getBrandName(appContext.state.configuration.product),
              })}
            </Typography>
          </Stack>
        </Modal>
      )}
      <Modal
        open={addDeviceDialogOpened}
        onClose={handleCloseAssociateDeviceDialogs}
        maxWidth="sm"
        fullWidth
        className="devices-add-modal"
        title={t(translations.devices.root.add_button)}
        preventCloseClick
        actions={[
          {
            label: t(translations.modal.cancel),
            type: 'destructive',
            onClick: handleCloseAssociateDeviceDialogs,
          },
          {
            label: t(translations.device_form.save_button),
            type: 'nondestructive',
            onClick: handleOpenAssociateDeviceConfirmation,
            disabled: !associateDevice,
          },
        ]}
      >
        <Box className="devices-add-modal-field">
          <FlatSelectDropdown
            label={t(translations.associate_device_form.identifier_input)}
            required
            value={
              associateDevice
                ? {
                    key: associateDevice.id || '',
                    label: associateDevice.serialNumber || associateDevice.macAddress || '',
                  }
                : undefined
            }
            options={
              !institutionDevices
                ? []
                : institutionDevices.map((device) => ({
                    key: device.id || '',
                    label: device.serialNumber || device.macAddress || '',
                  }))
            }
            onChange={(value) => {
              setAssociateDevice(
                institutionDevices?.find((device) => device.id === value.key) || undefined
              );
            }}
          />
        </Box>
      </Modal>
      <Modal
        open={confirmAssociateDeviceDialogOpened}
        onClose={handleCloseAssociateDeviceDialogs}
        maxWidth="sm"
        fullWidth
        className="devices-listing-modal"
        preventCloseClick
        actions={[
          {
            label: t(translations.modal.cancel),
            type: 'destructive',
            onClick: handleCloseAssociateDeviceDialogs,
          },
          {
            label: t(translations.modal.confirm),
            type: 'nondestructive',
            onClick: handleAssociateDevice,
            loading: isSubmitting,
          },
        ]}
      >
        <Stack>
          <Typography className="title">
            {t(translations.associate_device_form.save_dialog_title, {
              name: props.userName,
              device: associateDevice?.serialNumber || associateDevice?.macAddress || '',
            })}
          </Typography>
          <Typography className="subtitle">
            {t(translations.associate_device_form.save_dialog_message, {
              name: props.userName,
              brand: getBrandName(appContext.state.configuration.product),
            })}
          </Typography>
        </Stack>
      </Modal>
    </Box>
  );
};
